import React, { type FunctionComponent } from 'react'
import { css } from '@emotion/react'
import { useForm } from 'react-hook-form'
import moment from 'moment'
import {
  Location,
  Experiment,
  Details,
  ContentImage,
  FlyoutDestination,
  FlyoutText,
} from 'domains/banners/components/banner-form/components'
import { MerchandisingFormSection } from 'domains/merchandising/components/form-section/MerchandisingFormSection'
import { type FlyoutFormData, type FlyoutFormProps } from 'domains/banners/types/banner.types'
import {
  PlacementPlatformEnum,
  PlacementExperimentMethodEnum,
  PlacementDestinationTypeEnum,
  ButtonVariantEnum,
  BannerLocaleEnum,
  PlacementTypeEnum,
} from 'domains/banners/api'
import { MerchandisingFormActions } from 'domains/merchandising/components/form-actions/MerchandisingFormActions'
import { getTimezoneOptions } from 'domains/merchandising/utils/heroBannerUtils'

const outerContainer = css({
  alignItems: 'center',
  flexDirection: 'column',
  display: 'flex',
  flex: '1',
  position: 'relative',
  width: '100%',
})

const innerContainer = css({
  width: '100%',
})

// If no value exists (create), set the defaultValue to the user's computer/browser timezone
// or fallback to EST (-4) as a last resort.
const currentTimezone = moment.utc().parseZone().utcOffset() / 60 || -4
const defaultTimezone = getTimezoneOptions().find(tz => tz.offset === currentTimezone)?.value

const emptyDestination = () => ({
  type: PlacementDestinationTypeEnum.DoNotNavigate,
  target: '{"record_click_attempt":false}',
  displayValue: null,
  external: {
    url: '',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    open_in_new_tab: 'false',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    open_in_new_window: 'false',
  },
  storefront: {
    retailerId: '',
  },
  storeselector: {
    retailerIds: '',
  },
  pickupstoreselector: {
    showDistance: 'false',
  },
})

const emptyInitialState: FlyoutFormData = {
  bannerEnabled: 'false',
  enabled: false,
  bannerRank: '4',
  rank: 4,
  type: PlacementTypeEnum.StandardInformationModal,
  name: '',
  platform: [
    PlacementPlatformEnum.Mobile,
    PlacementPlatformEnum.Web,
    PlacementPlatformEnum.Ios,
    PlacementPlatformEnum.Android,
  ],
  experiment: {
    name: '',
    variant: '',
    accessMethod: PlacementExperimentMethodEnum.Assign,
  },
  primaryDestination: emptyDestination(),
  secondaryDestination: emptyDestination(),
  linkDestination: emptyDestination(),
  scheduling: {
    startDate: new Date().toISOString(),
    endDate: null,
    timezone: defaultTimezone,
  },
  titleText: {
    text: '',
    color: '',
  },
  descriptionText: {
    text: '',
    color: '',
  },
  disclaimerText: {
    text: '',
    color: '',
  },
  primaryButton: {
    label: 'Shop now',
    color: '',
    backgroundColor: '',
    styleVariant: ButtonVariantEnum.Primary,
  },
  secondaryButton: {
    label: '',
    color: '',
    backgroundColor: '',
    styleVariant: ButtonVariantEnum.Secondary,
  },
  linkButton: {
    label: '',
    color: '',
    backgroundColor: '',
    styleVariant: ButtonVariantEnum.Link,
  },

  images: {
    mobile: '',
    desktop: '',
  },
  locale: BannerLocaleEnum.EnUs,
}

export const FlyoutForm: FunctionComponent<FlyoutFormProps> = ({
  type,
  initialState = emptyInitialState,
  submitError,
  isSubmitting,
  onCancel,
  onFormSubmit,
}) => {
  const scheduling = {
    ...initialState.scheduling,
    ...(initialState.scheduling.timezone === '' && { timezone: defaultTimezone }),
  }
  const form = useForm<FlyoutFormData>({
    mode: 'onBlur',
    defaultValues: { ...initialState, type, scheduling },
  })
  const { handleSubmit, register } = form
  const onSubmit = (formData: FlyoutFormData) => onFormSubmit(formData, form)

  return (
    <div css={outerContainer} data-testid="banner-form">
      <form css={innerContainer} onSubmit={handleSubmit(onSubmit)}>
        <input name="type" ref={register} type="hidden" />
        <Details form={form} />
        <Location form={form} />
        <Experiment form={form} />

        <FlyoutDestination
          form={form}
          defaultDisplayValue={initialState.primaryDestination?.displayValue}
          buttonField="primaryButton"
          destinationField="primaryDestination"
        />
        <FlyoutDestination
          form={form}
          defaultDisplayValue={initialState.secondaryDestination?.displayValue}
          buttonField="secondaryButton"
          destinationField="secondaryDestination"
        />
        <FlyoutDestination
          form={form}
          defaultDisplayValue={initialState.linkDestination?.displayValue}
          buttonField="linkButton"
          destinationField="linkDestination"
        />

        <MerchandisingFormSection
          titleId="bannersDomain.banners.section.content.title"
          tooltipId={'bannersDomain.banners.form.content.text.tooltip'}
          renderAction
        >
          <FlyoutText form={form} />
          <ContentImage type={PlacementTypeEnum.StandardInformationModal} form={form} />
        </MerchandisingFormSection>

        <MerchandisingFormActions
          onCancel={onCancel}
          isLoading={isSubmitting}
          submitError={submitError}
        />
      </form>
    </div>
  )
}
